'use client';

import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

export default function FinalClosingSection() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  const productUrl = 'https://www.lavyre.com/cart';

  const handleBuyNow = () => {
    try {
      window.open(productUrl, '_blank');
    } catch (error) {
      console.error('Failed to open product URL:', error);
    }
  };

  return (
    <section
      ref={ref}
      className="py-12 sm:py-16 md:py-24 lg:py-32 bg-black"
    >
      <div className="max-w-4xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl text-white mb-6 sm:mb-8"
          >
            Don't Think. Act.
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-white/80 text-sm sm:text-base md:text-lg lg:text-xl mb-6 sm:mb-8 leading-relaxed"
          >
            Every day you wait is another day dealing with:
          </motion.p>

          {/* Pain Points */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="space-y-3 sm:space-y-4 mb-8 sm:mb-12"
          >
            <div className="flex items-center gap-3 text-white/70 text-xs sm:text-sm md:text-base lg:text-lg">
              <i className="ri-close-circle-line text-red-500 text-xl sm:text-2xl flex-shrink-0" />
              <span>Dead lighters at the worst moments</span>
            </div>
            <div className="flex items-center gap-3 text-white/70 text-xs sm:text-sm md:text-base lg:text-lg">
              <i className="ri-close-circle-line text-red-500 text-xl sm:text-2xl flex-shrink-0" />
              <span>Crushed cigarettes in your pocket</span>
            </div>
            <div className="flex items-center gap-3 text-white/70 text-xs sm:text-sm md:text-base lg:text-lg">
              <i className="ri-close-circle-line text-red-500 text-xl sm:text-2xl flex-shrink-0" />
              <span>Wasting money on disposable junk</span>
            </div>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-white text-base sm:text-lg md:text-xl lg:text-2xl mb-6 sm:mb-8 font-serif"
          >
            You have two choices:
          </motion.p>

          {/* Two Choices */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-8 sm:mb-12"
          >
            <div className="bg-white/5 border border-white/10 rounded-lg p-4 sm:p-6">
              <p className="text-white/60 text-xs sm:text-sm md:text-base leading-relaxed">
                Keep doing what you're doing and get the same frustrating results...
              </p>
            </div>
            <div className="bg-white/10 border border-white/20 rounded-lg p-4 sm:p-6">
              <p className="text-white text-xs sm:text-sm md:text-base leading-relaxed font-medium">
                Or get the last lighter you'll ever need.
              </p>
            </div>
          </motion.div>

          {/* Trust Badges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 mb-6 sm:mb-8 text-white/70 text-xs sm:text-sm md:text-base"
          >
            <div className="flex items-center gap-2">
              <i className="ri-shield-check-line text-base sm:text-lg md:text-xl" />
              <span>30-day money back guarantee</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-truck-line text-base sm:text-lg md:text-xl" />
              <span>Free shipping to your door</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-lock-line text-base sm:text-lg md:text-xl" />
              <span>Zero risk, all reward</span>
            </div>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="text-white/60 text-xs sm:text-sm md:text-base mb-6 sm:mb-8 italic"
          >
            The only risk is waiting and paying full price later.
          </motion.p>

          {/* Final CTA */}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.9 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleBuyNow}
            className="w-full max-w-md mx-auto bg-white text-black py-4 sm:py-5 rounded-full text-base sm:text-lg md:text-xl font-medium hover:bg-white/90 transition-all whitespace-nowrap cursor-pointer active:scale-[0.98] shadow-xl"
          >
            BUY NOW — $49.90
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
}
